import { environment } from './../../../environments/environment';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import firebase from 'firebase/app';
import AuthProvider = firebase.auth.AuthProvider;
import { AngularFireAuth } from '@angular/fire/auth';
import { AngularFirestore } from '@angular/fire/firestore';
import { map } from 'rxjs/operators';
import { AngularFireStorage } from '@angular/fire/storage';
import { ErrorMonitoringService } from '../error-monitoring/error-monitoring.service';
import { HelperService } from '../helper/helper.service';
import * as moment from 'moment-timezone';

@Injectable({
  providedIn: 'root'
})
export class UserDataService {

  private user: any;
  private userState = new BehaviorSubject<any>(null);
  public userStateObservable = this.userState.asObservable();
  public authToken = null;
  public deniedUrl = []; // guarda la url de redirección
  userDataRef = 'userProfile';

  constructor(
    private angularFireAuth: AngularFireAuth,
    private angularFirestore: AngularFirestore,
    private storage: AngularFireStorage,
    private errorMonitoringService: ErrorMonitoringService,
    private helperService: HelperService
  ) {
    // Se encarga de emitir el estado del user a los componentes que se subscriban al observable userStateObservable
    this.angularFireAuth.authState.subscribe(user => {
      if (user && !user.isAnonymous) {
        user.getIdToken(true).then(token => {
          this.authToken = token;
        }).catch(error => {
          this.errorMonitoringService.sendError(error, 'getIdToken');
        });
        this.user = user;
        this.getProfile(user.uid).subscribe(userData => {
          this.userState.next({ ...userData, user });
        }, error => {
          this.errorMonitoringService.sendError(error, 'getProfile');
        });
      } else {
        this.userState.next(user);
      }
    }, error => {
      this.errorMonitoringService.sendError(error, 'getUser');
      this.helperService.presentMessageAlert('Mensaje', 'No pudimos obtener tu usuario, inténtalo de nuevo');
    });
  }

  // devuelve el token de auth
  getAuthToken(): any {
    return this.authToken;
  }

  // obtiene el token de auth
  obtainAuthToken(): Promise<any> {
    return new Promise(resolve => {
      this.user.getIdToken(true).then(token => {
        resolve(token);
      }).catch(error => {
        this.errorMonitoringService.sendError(error, 'obtainAuthToken');
      });
    });
  }

  public emailLogin(credentials) {
    return this.angularFireAuth.signInWithEmailAndPassword(credentials.email, credentials.password);
  }

  public emailRegister(credentials) {
    return this.angularFireAuth.createUserWithEmailAndPassword(credentials.email, credentials.password);
  }

  public googleLogin() {
    const provider = new firebase.auth.GoogleAuthProvider();
    return this.authProviderLogin(provider);
  }

  public facebookLogin() {
    const provider = new firebase.auth.FacebookAuthProvider();
    return this.authProviderLogin(provider);
  }

  private authProviderLogin(provider: AuthProvider) {
    return this.angularFireAuth.signInWithPopup(provider);
  }

  public anonymouslySignIn() {
    return this.angularFireAuth.signInAnonymously();
  }

  public createProfile(credentials: any, userUID: string) {
    const profile = {
      name: credentials.name,
      surname: credentials.surname,
      mobile: credentials.mobile,
      mobileVerified: false,
      mobileSuffix: '',
      email: credentials.email,
      country: 'COL',
      registerDate: moment().tz('America/Bogota').format(),
      services: {
        state: 'active',
        registerDate: moment().tz('America/Bogota').format()
      }
    };
    // agrega la propiedad mobileSuffix
    if (credentials.country === 'COL') {
      profile.mobileSuffix = '57';
    }
    return this.angularFirestore.collection<any>(`userProfile`).doc(userUID).set(profile);
  }

  public updateProfile(profile: any, user: string) {

    const res = this.angularFirestore.collection<any>(`userProfile`).doc(`${user}`).update(profile);

    if (!profile.password) return res;

    var userActual = firebase.auth().currentUser;
    return userActual.updatePassword(profile.password);
  }

  // desvincula el acceso con celular
  public unlinkPhoneAccount() {
    return this.user.unlink(firebase.auth.PhoneAuthProvider.PROVIDER_ID);
  }

  // obtiene el perfil del user
  public getProfile(id: string) {
    return this.angularFirestore.doc<any>(`userProfile/${id}`).snapshotChanges().pipe(
      map(userData => {
        const data = userData.payload.data();
        const uid = userData.payload.id;
        if (data) {
          return { uid, ...data };
        }
      })
    );
  }

  // obtiene el perfil de un user por uid
  getProfileByUid(uid: string) {
    return this.angularFirestore.collection<any>('userProfile').doc(uid).get().toPromise().then(user => {
      return user.data();
    });
  }

  // obtiene el perfil por código de referido
  getProfileByReferralCode(code: string) {
    return this.angularFirestore.collection<any>(
      'userProfile', ref => ref.where('referralCode', '==', code)
    ).get();
  }

  // reestablecer contraseña password
  recoveryPassword(email: string) {
    return this.angularFireAuth.sendPasswordResetEmail(email, {
      url: environment.baseUrl
    });
  }

  // upload image
  uploadImage(client: string, image: any) {
    return new Promise((resolve, reject) => {
      let filePath;
      // Ruta en la que se almacena
      filePath = `client_images/${client}/${image.name}`;
      // Almacena la imagen
      this.storage.upload(filePath, image).then(snapshot => {
        // Obtiene la url de descarga
        snapshot.ref.getDownloadURL().then((downloadURL) => {
          resolve(downloadURL);
        });
      }).catch(error => {
        this.errorMonitoringService.sendError(error, 'uploadImage');
        reject();
      });
    });
  }

  public signOut() {
    return this.angularFireAuth.signOut();
  }

  // retorna el user
  getUser(): any {
    return this.user;
  }

  // Devuelve el id del cliente
  getUserId() {
    if (this.user) {
      return this.user.uid;
    }
  }

  // retorna el observable de auth
  get currentUserObservable(): any {
    return this.angularFireAuth.authState;
  }

  // guarda una url de redirección en caso que authguard no le haya permitido ingresar
  // para redirigir nuevamente al user a lo que buscaba cuando haga login
  saveUrlDeniedLogin(url: Array<string>): void {
    this.deniedUrl = url;
  }

  // retorna la url
  get getDeniedUrl(): any {
    return this.deniedUrl;
  }

  // reestablece la deniedUrl
  clearDeniedUrl(): void {
    this.deniedUrl = [];
  }

  // Registra al usuario en nuevos leads
  registerLead(data: any) {
    return this.angularFirestore.collection<any>('leads').add(data);
  }

  // obtiene todos los leads registrados por un usuario
  getLeadsByReferral(code: string) {
    return this.angularFirestore.collection<any>(
      'leads', ref => ref.where('referral', '==', code)
      .where('date', '>', moment().startOf('week').format())
      .orderBy('date', 'desc')
    )
    .snapshotChanges().pipe(
      map( leads => {
        return leads.map( lead => {
          const data = lead.payload.doc.data();
          const id = lead.payload.doc.id;
          return { id, ...data };
        });
      })
    );
  }

  // obtiene todos los leads registrados en la ultima semana
  getLeads() {
    return this.angularFirestore.collection<any>(
      'leads', ref => ref.where('date', '>', moment().startOf('week').format())
      .orderBy('date', 'desc')
    )
    .snapshotChanges().pipe(
      map( leads => {
        return leads.map( lead => {
          const data = lead.payload.doc.data();
          const id = lead.payload.doc.id;
          return { id, ...data };
        });
      })
    );
  }

  // actualiza el lead
  updateLead(lead: string, data: any) {
    return this.angularFirestore.collection<any>('leads').doc(`${lead}`).update(data);
  }

  // obtiene el perfil de un user por correo
  getProfileByEmail(email: string) {
    return this.angularFirestore.collection<any>(this.userDataRef, ref => ref.where('email', '==', email))
      .snapshotChanges().pipe(
        map(users => {
          return users.map(user => {
            const data = user.payload.doc.data();
            const id = user.payload.doc.id;
            return { id, ...data };
          });
        })
      );
  }

  // obtiene todos los mediamigos disponibles según
  // el servicio
  getMedifriends(type: string) {
    return this.angularFirestore.collection<any>(
      this.userDataRef, ref => ref.where(`${type}`, '==', 'true'))
      .snapshotChanges().pipe(
        map(userProfiles => {
          return userProfiles.map(userProfile => {
            const data = userProfile.payload.doc.data();
            const id = userProfile.payload.doc.id;
            return { id, ...data };
          });
        })
      );
  }

  // obtiene las ventas de un asesor
  getSales(referralCode: string) {
    return this.angularFirestore.collection<any>(
      this.userDataRef,
      ref => ref.where('referralCode', '==', referralCode).orderBy(`telemedicina.registerDate`, 'asc').limit(10)
    ).snapshotChanges().pipe(
      map( sales => {
        return sales.map( sale => {
          const docRef = sale.payload.doc;
          const data = sale.payload.doc.data();
          const id = sale.payload.doc.id;
          return { id, ...data, docRef };
        });
      })
    );
  }

  // obtiene más ventas del asesor
  getMoreSales(referralCode: string, doc: any) {
    return this.angularFirestore.collection<any>(
      this.userDataRef,
      ref => ref.where('referralCode', '==', referralCode).orderBy(`telemedicina.registerDate`, 'asc')
      .limit(5).startAfter(doc)
    ).snapshotChanges().pipe(
      map( sales => {
        return sales.map( sale => {
          const docRef = sale.payload.doc;
          const data = sale.payload.doc.data();
          const id = sale.payload.doc.id;
          return { id, ...data, docRef };
        });
      })
    );
  }

}
